import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["availableOperation", "secondaryButtonUse", "primaryButtonUse"];
import { useMemo } from 'react';
import { Button } from 'foundations-components/transitional/components/Button';
import { ApprovalOperationI18n } from '../i18n/ApprovalOperationI18n';
import { withErrorBoundary } from '../../hoc/withErrorBoundary';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { useApprovalContext } from '../../hooks/useApprovalContext';
import { ApprovalOperation } from '../../types/ApprovalOperation';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useApprovalConditions } from '../../hooks/useApprovalConditions';
import { ApprovalDataLoadingSpinner } from '../common/ApprovalDataLoadingSpinner';
import { jsx as _jsx } from "react/jsx-runtime";
const ApprovalButton = _ref => {
  let {
      availableOperation,
      secondaryButtonUse,
      primaryButtonUse
    } = _ref,
    buttonProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const approval = useApprovalContext();
  const {
    isFetchComplete
  } = useApprovalConditions();

  // The meta.approvalOperation is used to support the ContenttEditorUI - they use their own
  // logic instead of the ApprovalButtons, and they do not currently use 'REJECT'.
  // (ContentEditorUI/static/js/components/approvals/EditorApprovalButtons.tsx)
  const approvalOperation = availableOperation || approval.meta.approvalOperation;
  const use = useMemo(() => {
    if (approvalOperation === ApprovalOperation.CANCEL) {
      return secondaryButtonUse || 'secondary';
    }
    if (approvalOperation === ApprovalOperation.REJECT) {
      return secondaryButtonUse || 'secondary';
    }
    return primaryButtonUse || 'primary';
  }, [approvalOperation, primaryButtonUse, secondaryButtonUse]);
  const onClickButton = () => {
    approval.meta.setCurrentOperation(approvalOperation);
    approval.meta.setShowModal(true);
  };

  // Added loading check here as some apps are using ApprovalButton directly
  if (!isFetchComplete) {
    return /*#__PURE__*/_jsx(ApprovalDataLoadingSpinner, {});
  }
  if (!approvalOperation) {
    return null;
  }
  const isCancelOperation = approvalOperation === ApprovalOperation.CANCEL;
  const isButtonDisabled = Boolean(buttonProps.disabled);
  const tooltipTitle = isButtonDisabled ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "productApprovals.noAccessTooltip"
  }) : isCancelOperation ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "productApprovals.cancelOperationTooltip"
  }) : null;
  const isTooltipDisabled = !isButtonDisabled && !isCancelOperation;
  return /*#__PURE__*/_jsx(Tooltip, {
    title: tooltipTitle,
    disabled: isTooltipDisabled,
    children: /*#__PURE__*/_jsx(Button, Object.assign({
      role: "button"
    }, buttonProps, {
      use: use,
      "data-test-id": `product-approvals-approval-button-${approvalOperation}`,
      onClick: onClickButton,
      children: /*#__PURE__*/_jsx(ApprovalOperationI18n, {
        approvalOperation: approvalOperation,
        approvalType: approval.meta.approvalType
      })
    }))
  });
};
const ApprovalButtonWithErrorBoundary = withErrorBoundary(ApprovalButton, 'ApprovalButton');
export { ApprovalButtonWithErrorBoundary as ApprovalButton };